import React, { useState, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { Sidebar } from './components/layout/Sidebar';
import { WhatsAppWidget } from './components/ui/WhatsAppWidget';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { ConnectionBanner } from './components/ui/ConnectionBanner';
import { useAuthStore } from './store/authStore';
import { useOrderStore } from './store/orderStore';
import { supabase } from './services/supabase';
import { initGA, trackPageView } from './services/analytics';
import './App.css';

// Lazy Load Pages
const LandingPage = React.lazy(() => import('./pages/landing/Landing').then(m => ({ default: m.LandingPage })));
const RegisterPage = React.lazy(() => import('./pages/auth/Register').then(m => ({ default: m.RegisterPage })));
const LoginPage = React.lazy(() => import('./pages/auth/Login').then(m => ({ default: m.LoginPage })));
const FAQPage = React.lazy(() => import('./pages/faq/FAQ').then(m => ({ default: m.FAQPage })));
const ContactPage = React.lazy(() => import('./pages/contact/Contact').then(m => ({ default: m.ContactPage })));
const AboutPage = React.lazy(() => import('./pages/about/About').then(m => ({ default: m.AboutPage })));
const ProductsPage = React.lazy(() => import('./pages/products/Products').then(m => ({ default: m.ProductsPage })));
const ProductDetailsPage = React.lazy(() => import('./pages/products/ProductDetails').then(m => ({ default: m.ProductDetailsPage })));
const FavoritesPage = React.lazy(() => import('./pages/favorites/Favorites').then(m => ({ default: m.FavoritesPage })));
const CartPage = React.lazy(() => import('./pages/cart/Cart').then(m => ({ default: m.CartPage })));
const CheckoutPage = React.lazy(() => import('./pages/checkout/Checkout').then(m => ({ default: m.CheckoutPage })));
const OrdersPage = React.lazy(() => import('./pages/orders/Orders').then(m => ({ default: m.OrdersPage })));
const OrderDetailPage = React.lazy(() => import('./pages/orders/OrderDetail').then(m => ({ default: m.OrderDetailPage })));
const SettingsPage = React.lazy(() => import('./pages/settings/Settings').then(m => ({ default: m.SettingsPage })));
const ProfilePage = React.lazy(() => import('./pages/profile/Profile').then(m => ({ default: m.ProfilePage })));
const ReviewsPage = React.lazy(() => import('./pages/reviews/Reviews').then(m => ({ default: m.ReviewsPage })));
const CouponsPage = React.lazy(() => import('./pages/coupons/Coupons').then(m => ({ default: m.CouponsPage })));
const SamplesPage = React.lazy(() => import('./pages/samples/Samples').then(m => ({ default: m.SamplesPage })));
const HelpPage = React.lazy(() => import('./pages/help/Help').then(m => ({ default: m.HelpPage })));
const AdminDashboardPage = React.lazy(() => import('./pages/admin/dashboard/AdminDashboard').then(m => ({ default: m.AdminDashboardPage })));
const AdminOrdersPage = React.lazy(() => import('./pages/admin/orders/AdminOrders').then(m => ({ default: m.AdminOrdersPage })));
const AdminCustomersPage = React.lazy(() => import('./pages/admin/customers/AdminCustomers').then(m => ({ default: m.AdminCustomersPage })));
const CustomerOrdersPage = React.lazy(() => import('./pages/admin/customers/CustomerOrders').then(m => ({ default: m.CustomerOrdersPage })));
const AdminReportsPage = React.lazy(() => import('./pages/admin/reports/AdminReportsEnhanced').then(m => ({ default: m.AdminReportsPage })));
const AdminProductsPage = React.lazy(() => import('./pages/admin/stock/AdminStockEnhanced').then(m => ({ default: m.default })));
const AdminCampaignsPage = React.lazy(() => import('./pages/admin/campaigns/AdminCampaigns').then(m => ({ default: m.AdminCampaignsPage })));
const AdminMembershipPage = React.lazy(() => import('./pages/admin/membership/AdminMembership').then(m => ({ default: m.AdminMembershipPage })));
const AdminAccountingPage = React.lazy(() => import('./pages/admin/accounting/AdminAccountingEnhanced').then(m => ({ default: m.default })));
const AuthCallbackPage = React.lazy(() => import('./pages/auth/AuthCallback').then(m => ({ default: m.AuthCallbackPage })));
const ConnectionTestPage = React.lazy(() => import('./pages/admin/test/ConnectionTest').then(m => ({ default: m.ConnectionTest })));
const NotFoundPage = React.lazy(() => import('./pages/errors/NotFound').then(m => ({ default: m.NotFoundPage })));
const ServerErrorPage = React.lazy(() => import('./pages/errors/ServerError').then(m => ({ default: m.ServerErrorPage })));

// Loading Component
const LoadingSpinner = () => (
  <div className="flex-center" style={{ height: '100vh', flexDirection: 'column', gap: '1rem' }}>
    <div className="spinner" style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #e91e63',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <span style={{ color: '#666', fontSize: '0.9rem' }}>Yükleniyor...</span>
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

// Logout Component
const Logout = () => {
  const { logout } = useAuthStore();
  useEffect(() => {
    const performLogout = async () => {
      try {
        await logout();
        await supabase.auth.signOut();
      } catch (e) {
        console.warn('Logout warning:', e);
      }
      useAuthStore.setState({ user: null, token: null, isAuthenticated: false });
      window.location.href = '/login';
    };
    performLogout();
  }, [logout]);

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '1rem',
      background: '#f8f9fa'
    }}>
      <LoadingSpinner />
      <h3 style={{ color: '#333' }}>Güvenli çıkış yapılıyor...</h3>
    </div>
  );
};

// Protected Route Wrapper
const ProtectedRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// Admin Route Wrapper
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ width: '50px', height: '50px', border: '4px solid #f3f3f3', borderTop: '4px solid #e91e63', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

// Layout with Navbar (Public)
const PublicLayout: React.FC = () => {
  return (
    <div className="public-layout">
      <Navbar />
      <main className="public-content">
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

// Layout with Sidebar (Protected - Customer)
const CustomerLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <ConnectionBanner />
        <div className="page-container">
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
};

// Layout with Sidebar (Protected - Admin)
const AdminLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <ConnectionBanner />
        <div className="page-container">
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
};

// Conditional WhatsApp Widget
const ConditionalWhatsAppWidget: React.FC = () => {
  const location = useLocation();
  const isAdminPanel = location.pathname.startsWith('/admin');
  if (isAdminPanel) return null;
  return <WhatsAppWidget />;
};

function App() {
  const { user, subscribeToProfileChanges, unsubscribeFromProfileChanges } = useAuthStore();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    if (user && user.role === 'customer') {
      subscribeToProfileChanges();
      return () => {
        unsubscribeFromProfileChanges();
      };
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      const { subscribeToOrderChanges, unsubscribeFromOrderChanges } = useOrderStore.getState();
      subscribeToOrderChanges();
      return () => {
        unsubscribeFromOrderChanges();
      };
    }
  }, [user?.id]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error || !session) {
          const currentState = useAuthStore.getState();
          if (currentState.user && currentState.isAuthenticated) {
            useAuthStore.setState({ isLoading: false });
          } else {
            useAuthStore.setState({ isLoading: false, isAuthenticated: false, user: null });
          }
          return;
        }

        const user = session.user;
        const { data: profile } = await supabase.from('profiles').select('*').eq('id', user.id).single();

        if (profile) {
          useAuthStore.setState({
            user: {
              id: user.id,
              email: user.email || '',
              firstName: profile.first_name || '',
              lastName: profile.last_name || '',
              fullName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email || '',
              companyName: profile.company_name || '',
              companyCode: profile.company_code,
              phone: profile.phone || '',
              role: profile.role || 'customer',
              addresses: [],
              createdAt: new Date(profile.created_at),
            },
            token: session.access_token,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } catch (err) {
        useAuthStore.setState({ isLoading: false });
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        const currentState = useAuthStore.getState();
        if (!currentState.user && !currentState.isAuthenticated) {
          return;
        }
        useAuthStore.setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary fallback={<ServerErrorPage />}>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster
            position="top-right"
            reverseOrder={false}
            toastOptions={{
              duration: 3500,
              className: 'glassmorph-toast-wrapper',
              style: {
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: '#fff',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                fontSize: '14px',
                fontWeight: 500,
                maxWidth: '380px',
                minWidth: '260px',
              },
              success: {
                style: {
                  background: 'rgba(16, 185, 129, 0.2)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  color: '#10b981',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15)',
                },
                iconTheme: {
                  primary: '#10b981',
                  secondary: 'rgba(16, 185, 129, 0.1)',
                },
              },
              error: {
                style: {
                  background: 'rgba(239, 68, 68, 0.2)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  boxShadow: '0 8px 32px rgba(239, 68, 68, 0.15)',
                },
                iconTheme: {
                  primary: '#ef4444',
                  secondary: 'rgba(239, 68, 68, 0.1)',
                },
              },
            }}
          />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:slug" element={<ProductDetailsPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/contact" element={<ContactPage />} />
              </Route>

              <Route path="/login" element={<LoginPage />} />
              {/* Register kapalı - sadece admin ekleyebilir */}
              <Route path="/register" element={<Navigate to="/login" replace />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/logout" element={<Logout />} />

              <Route element={<ProtectedRoute><PublicLayout /></ProtectedRoute>}>
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/samples" element={<SamplesPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/orders/:id" element={<OrderDetailPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/coupons" element={<CouponsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/help" element={<HelpPage />} />
              </Route>

              <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/orders" element={<AdminOrdersPage />} />
                <Route path="/admin/stock" element={<AdminProductsPage />} />
                <Route path="/admin/customers" element={<AdminCustomersPage />} />
                <Route path="/admin/customers/:customerId/orders" element={<CustomerOrdersPage />} />
                <Route path="/admin/reports" element={<AdminReportsPage />} />
                <Route path="/admin/campaigns" element={<AdminCampaignsPage />} />
                <Route path="/admin/membership" element={<AdminMembershipPage />} />
                <Route path="/admin/accounting" element={<AdminAccountingPage />} />
                <Route path="/admin/test" element={<ConnectionTestPage />} />
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>

          <ConditionalWhatsAppWidget />
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
